// Registry per i componenti A-Frame - evita registrazioni multiple
import {characterMoveComponent, characterRecenterComponent} from './components'
import { applyCubemapComponent } from './apply-cubemap'
import { registerChromakeyShader } from './chromakey-shader'
import { playVideoCorrectComponent, holdDragComponent, lookAtComponent } from './ologramma-components'

// Set per tenere traccia dei componenti già registrati
const registeredComponents = new Set()

// Funzione per registrare un componente solo se non è già registrato
const registerComponentOnce = (name, component) => {
  // Verifica che AFRAME sia disponibile prima di registrare
  if (typeof AFRAME === 'undefined') {
    console.warn(`⚠️ AFRAME non disponibile, componente "${name}" non registrato`)
    return
  }
  
  if (!registeredComponents.has(name)) {
    try {
      AFRAME.registerComponent(name, component)
      registeredComponents.add(name)
      console.log(`Componente A-Frame "${name}" registrato`)
    } catch (error) {
      console.error(`❌ Errore nella registrazione del componente "${name}":`, error)
    }
  } else {
    console.log(`Componente A-Frame "${name}" già registrato, skip`)
  }
}

// Registra tutti i componenti necessari
export const registerAllComponents = () => {
  registerComponentOnce('character-move', characterMoveComponent)
  registerComponentOnce('character-recenter', characterRecenterComponent)
  registerComponentOnce('apply-cubemap', applyCubemapComponent())
  
  registerComponentOnce('no-cull', {
    init() {
      this.el.addEventListener('model-loaded', () => {
        this.el.object3D.traverse(obj => obj.frustumCulled = false)
      })
    },
  })

  // Sky Remote Authoring component per aquileSky - gestione ambiente e sky layer
  registerComponentOnce('sky-remote-authoring', {
    schema: {
      foreground: {type: 'boolean', default: true},
    },
    init() {
      console.log('🌌 Sky Remote Authoring component INITIALIZED!');
      let cylinder;
      
      // reparent elements from sky-scene to the regular a-scene
      const skyScene = document.querySelector('[xrlayerscene]')
      if (skyScene) {
        console.log('🌌 Reparenting sky-scene elements to main scene');
        Array.from(skyScene.children).forEach((child) => {
          this.el.object3D.add(child.object3D)
        })
      } else {
        console.warn('⚠️ No xrlayerscene found');
      }

      // Remove mobile-specific components from scene
      setTimeout(() => {
        this.el.removeAttribute('xrlayers')
        console.log('🌌 Removed xrlayers attribute');
      }, 0)
      this.el.removeAttribute('xrextras-loading')
      this.el.removeAttribute('xrextras-runtime-error')
      this.el.removeAttribute('landing-page')
      this.el.removeAttribute('sky-coaching-overlay')
      console.log('🌌 Removed mobile-specific attributes');

      // Create the cylinder element that the foreground is attached to
      cylinder = document.createElement('a-cylinder')
      cylinder.setAttribute('open-ended', true)
      cylinder.setAttribute('material', 'depthTest:false; fog:false; opacity:.96')
      cylinder.setAttribute('transparent', true)
      cylinder.setAttribute('src', 'https://cdn.8thwall.com/web/projects/foreground.svg')
      cylinder.setAttribute('side', 'back')
      cylinder.setAttribute('position', '0 -.5 0')
      cylinder.setAttribute('scale', '100 350 100')
      console.log('🌌 Created foreground cylinder');

      // Create the plane element
      const plane = document.createElement('a-plane')
      plane.setAttribute('material', 'src: https://cdn.8thwall.com/web/projects/ground.svg; repeat:1500 15000;')
      plane.setAttribute('color', '#808080')
      plane.setAttribute('height', '1000')
      plane.setAttribute('width', '100')
      plane.setAttribute('scale', '100 100 100')
      plane.setAttribute('rotation', '-90 0 0')
      plane.setAttribute('position', '0 0 0')
      console.log('🌌 Created ground plane');

      // raise camera to human height to mimic eye-level
      const camera = document.querySelector('a-camera')
      if (camera) {
        camera.setAttribute('position', '0 2 0')
        console.log('🌌 Raised camera to human eye level');
      }

      // Add fog element
      this.el.setAttribute('fog', 'type: exponential; color: #AAA; density:0.01')
      console.log('🌌 Added atmospheric fog');

      // Attach the elements to the scene
      this.el.appendChild(cylinder)
      this.el.appendChild(plane)
      console.log('🌌 Sky remote authoring setup complete');
      
      // Store cylinder reference for updates
      this.cylinder = cylinder;
    },
    update(oldData) {
      // allows you to toggle foreground in the aframe inspector
      if (this.data.foreground !== oldData.foreground) {
        if (this.cylinder) {
          if (this.data.foreground) {
            this.cylinder.setAttribute('visible', true)
            console.log('🌌 Foreground cylinder shown');
          } else {
            this.cylinder.setAttribute('visible', false)
            console.log('🌌 Foreground cylinder hidden');
          }
        }
      }
    },
  });

  // Arrows component per aquileSky - rotazione cielo + animazioni modello come originale 8thwall
  registerComponentOnce('arrows-component', {
    init() {
      const scene = this.el
      const pivot = document.querySelector('#pivot')
      const airshipModel = document.querySelector('[gltf-model="#airship"]')
      let rightWalkingInterval
      let leftWalkingInterval

      const bottomBar = document.getElementById('bottomBar')
      const rightButton = document.getElementById('rightButton')
      const leftButton = document.getElementById('leftButton')

      scene.addEventListener('sky-coaching-overlay.hide', () => {
        if (bottomBar) bottomBar.style.display = 'flex'
      })

      // The current value of the pivot's 'rotation' attribute
      let currentSkyPosition
      // The updated value of the pivot's 'rotation' attribute
      let updatedSkyPosition

      if (rightButton) {
        rightButton.addEventListener('touchstart', (e) => {
          rightWalkingInterval = setInterval(() => {
            if (pivot) {
              currentSkyPosition = pivot.getAttribute('rotation')

              // Subtract .5 from the "y" rotation of the pivot
              updatedSkyPosition = {
                x: currentSkyPosition.x,
                y: currentSkyPosition.y - 0.5,
                z: currentSkyPosition.z,
              }
              // Set the updated pivot 'rotation' attribute
              pivot.setAttribute('rotation', updatedSkyPosition)
            }
          }, 25)
          
          if (airshipModel && airshipModel.object3D) {
            airshipModel.object3D.rotation.y = Math.PI / 3
            airshipModel.setAttribute('animation-mixer', {clip: 'walk'})
          }

          e.preventDefault()
        })

        rightButton.addEventListener('touchend', () => {
          if (airshipModel) {
            airshipModel.setAttribute('animation-mixer', {clip: 'idle'})
            if (airshipModel.object3D) airshipModel.object3D.rotation.y = 0
          }
          clearInterval(rightWalkingInterval)
        })
      }

      if (leftButton) {
        leftButton.addEventListener('touchstart', (e) => {
          leftWalkingInterval = setInterval(() => {
            if (pivot) {
              currentSkyPosition = pivot.getAttribute('rotation')

              // Add .5 to the 'y' rotation of the pivot
              updatedSkyPosition = {
                x: currentSkyPosition.x,
                y: currentSkyPosition.y + 0.5,
                z: currentSkyPosition.z,
              }
              // Set the updated pivot 'rotation' attribute
              pivot.setAttribute('rotation', updatedSkyPosition)
            }
          }, 25)
          
          if (airshipModel && airshipModel.object3D) {
            airshipModel.object3D.rotation.y = -(Math.PI / 3)
            airshipModel.setAttribute('animation-mixer', {clip: 'walk'})
          }

          e.preventDefault()
        })

        leftButton.addEventListener('touchend', () => {
          if (airshipModel) {
            airshipModel.setAttribute('animation-mixer', {clip: 'idle'})
            if (airshipModel.object3D) airshipModel.object3D.rotation.y = 0
          }
          clearInterval(leftWalkingInterval)
        })
      }

      // Prevent double tap zoom
      document.ondblclick = function (e) {
        e.preventDefault()
      }
    }
  });

  // Device orientation component per aquileSky - con controllo orientamento verso il cielo
  registerComponentOnce('device-orientation-aquile', {
    init() {
      const orientationOverlay = document.getElementById('orientationOverlay')
      const skyPointingOverlay = document.getElementById('skyPointingOverlay')
      
      let isPortrait = true
      let isPointingUp = false
      
      const updateOverlays = () => {
        if (!isPortrait) {
          // Landscape mode - show rotation overlay
          orientationOverlay.style.display = 'flex'
          skyPointingOverlay.style.display = 'none'
        } else if (!isPointingUp) {
          // Portrait but not pointing up - show sky pointing overlay
          orientationOverlay.style.display = 'none'
          skyPointingOverlay.style.display = 'flex'
        } else {
          // Portrait and pointing up - hide all overlays
          orientationOverlay.style.display = 'none'
          skyPointingOverlay.style.display = 'none'
        }
      }
      
      const checkOrientation = (orientation) => {
        isPortrait = orientation === 0
        updateOverlays()
      }
      
      // Device motion for checking if pointing up
      if (window.DeviceOrientationEvent) {
        window.addEventListener('deviceorientation', (event) => {
          const beta = event.beta // front-to-back tilt (-180 to 180)
          
          // Device is pointing up when beta is less than -30 degrees (more upward)
          isPointingUp = beta > 90
          updateOverlays()
        })
      }
      
      XR8.addCameraPipelineModule({
        name: 'orientation',
        onStart: ({orientation}) => {
          checkOrientation(orientation)
        },
        onDeviceOrientationChange: ({orientation}) => {
          checkOrientation(orientation)
        },
      })
    },
  });

  // Sky debug component per aquileSky - come originale 8thwall
  registerComponentOnce('sky-debug', {
    init() {
      const style = document.createElement('style')
      style.innerHTML = `
        #debugOptions {
          display: flex;
          flex-direction: column;
          position: absolute;
          bottom: 10vh;
          right: 5px;
          z-index: 10;
          align-items: center;
          -webkit-user-select: none; /* Safari */        
          user-select: none; /* Standard */
        }

        .debugImg {
          width: 100%;
          height: 100%;
          max-height: 35px;
          background-repeat: no-repeat;
          background-size: contain;
          background-position: bottom;
          filter: drop-shadow(0px 0px 4px rgba(0,0,0,0.25));
          pointer-events: none;
        }

        .debugTxt {
          font-family: 'Nunito', sans-serif;
          pointer-events: none;
          font-size: 14px;
          color: white;
          line-height: 15px;
          text-align: center;
          margin-top: 0;
          text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
        }
      `

      let invertMaskBoolean = false
      let swapTextureBoolean = true

      const swapTextureIcon = 'https://iscag-ei.it/wp-content/materiale/swapTexture.svg'
      const invertMaskIcon = 'https://iscag-ei.it/wp-content/materiale/invertMask.svg'
      const recenterIcon = 'https://iscag-ei.it/wp-content/materiale/recenter.svg'

      const skybox = this.el.querySelector('a-sky')

      const handleInvertMask = () => {
        invertMaskBoolean = !invertMaskBoolean
        XR8.LayersController.configure({layers: {sky: {invertLayerMask: invertMaskBoolean}}})
      }

      const handleSwapTexture = () => {
        swapTextureBoolean = !swapTextureBoolean
        swapTextureBoolean ? skybox.setAttribute('material', 'opacity: 0') : skybox.setAttribute('material', 'opacity: 1')
      }

      const handleRecenter = () => {
        XR8.LayersController.recenter()
      }

      // Debug options rimossi per esperienza pulita
    }
  });

  // Sky recenter component per aquileSky - come originale 8thwall
  registerComponentOnce('sky-recenter', {
    init() {
      const recenter = () => {
        this.el.emit('recenter')
        this.el.removeEventListener('sky-coaching-overlay.hide', recenter)
      }
      this.el.addEventListener('sky-coaching-overlay.hide', recenter)
    }
  });

  // Debug visivo per aquileSky
  registerComponentOnce('aquile-debug-overlay', {
    init() {
      console.log('🐛 Aquile debug overlay INITIALIZED!');
      
      const updateDebugInfo = () => {
        const debugScale = document.getElementById('debugScale');
        const debugPosition = document.getElementById('debugPosition'); 
        const debugRotation = document.getElementById('debugRotation');
        const debugModelPath = document.getElementById('debugModelPath');
        const debugSkyCoaching = document.getElementById('debugSkyCoaching');
        const debugLayerState = document.getElementById('debugLayerState');
        
        // Debug dell'elemento aquila nel DOM
        const airshipElement = document.querySelector('#airship, [gltf-model*="aquile"]');
        if (airshipElement) {
          const actualPosition = airshipElement.getAttribute('position') || 'non trovato';
          const actualScale = airshipElement.getAttribute('scale') || 'non trovato';
          const actualRotation = airshipElement.getAttribute('rotation') || 'non trovato';
          const actualModel = airshipElement.getAttribute('gltf-model') || 'non trovato';
          
          if (debugPosition) debugPosition.textContent = 'Position: ' + actualPosition;
          if (debugScale) debugScale.textContent = 'Scale: ' + actualScale;
          if (debugRotation) debugRotation.textContent = 'Rotation: ' + actualRotation;
          if (debugModelPath) debugModelPath.textContent = 'Model: ' + actualModel;
          
          console.log('🦅 Aquila element found:', {
            position: actualPosition,
            scale: actualScale,
            visible: airshipElement.object3D ? airshipElement.object3D.visible : 'N/A'
          });
        } else {
          if (debugPosition) debugPosition.textContent = 'Position: ELEMENTO NON TROVATO';
          if (debugScale) debugScale.textContent = 'Scale: ELEMENTO NON TROVATO';
          if (debugRotation) debugRotation.textContent = 'Rotation: ELEMENTO NON TROVATO';
          if (debugModelPath) debugModelPath.textContent = 'Model: ELEMENTO NON TROVATO';
          
          console.log('❌ Nessun elemento aquila trovato nel DOM');
        }
        
        // Monitora sky coaching overlay
        const skyCoaching = document.querySelector('[sky-coaching-overlay]');
        const skyCoachingVisible = skyCoaching && !skyCoaching.hasAttribute('hidden');
        if (debugSkyCoaching) {
          debugSkyCoaching.textContent = 'Sky Coaching: ' + (skyCoachingVisible ? 'VISIBILE' : 'NASCOSTO');
        }
        
        // Monitora layer state
        if (typeof XR8 !== 'undefined' && XR8.LayersController) {
          try {
            const layersState = XR8.LayersController.getLayersState();
            if (debugLayerState) {
              debugLayerState.textContent = 'Layer State: ' + (layersState && layersState.sky ? 
                (layersState.sky.active ? 'SKY ATTIVO' : 'sky inattivo') : 'non disponibile');
            }
          } catch (e) {
            if (debugLayerState) debugLayerState.textContent = 'Layer State: errore';
          }
        } else {
          if (debugLayerState) debugLayerState.textContent = 'Layer State: XR8 non disponibile';
        }
      };
      
      // Aggiorna debug info ogni 2 secondi
      const debugInterval = setInterval(updateDebugInfo, 2000);
      setTimeout(updateDebugInfo, 1000); // Prima verifica rapida
      
      // Cleanup
      this.el.addEventListener('componentremoved', () => {
        if (debugInterval) clearInterval(debugInterval);
      });
    }
  });

  // Componente per debug AR
  registerComponentOnce('ar-debug-system', {
    init: function() {
      // Funzione per scrivere nel pannello debug
      const debugLog = (message, type = 'info') => {
        const debugContent = document.getElementById('debugContent');
        if (debugContent) {
          const timestamp = new Date().toLocaleTimeString();
          const colors = {
            info: '#00ff00',
            warn: '#ffff00', 
            error: '#ff0000',
            success: '#00ffff'
          };
          const color = colors[type] || colors.info;
          
          const logEntry = document.createElement('div');
          logEntry.style.color = color;
          logEntry.style.marginBottom = '2px';
          logEntry.innerHTML = `[${timestamp}] ${message}`;
          
          debugContent.appendChild(logEntry);
          debugContent.scrollTop = debugContent.scrollHeight;
          
          // Mantieni solo gli ultimi 20 log
          while (debugContent.children.length > 20) {
            debugContent.removeChild(debugContent.firstChild);
          }
        }
        
        // Mantieni anche il console.log originale
        console.log(message);
      };

      debugLog('🚀 AR Debug Component Init');
      
      const scene = this.el;
      const singleVideo = document.querySelector('#video');
      const multiVideos = document.querySelectorAll('video[id^="video"]');
      const totalVideos = singleVideo ? 1 : multiVideos.length;
      
      debugLog('🎬 A-Scene: CONNECTED');
      debugLog('🎥 Video trovati: ' + totalVideos + ' (' + (singleVideo ? 'single' : 'multi') + '-target)');
      
      // Funzioni per gestire lo scan
      const hideImage = () => {
        debugLog('👁️ Nascondendo immagine scan', 'success');
        const targetImageOverlay = document.getElementById('targetImageOverlay');
        if (targetImageOverlay) {
          targetImageOverlay.style.display = 'none';
          debugLog('✅ Scan nascosto');
        } else {
          debugLog('❌ targetImageOverlay non trovato', 'error');
        }
        
        // Cerca e nascondi anche eventuali elementi di scan
        const scanElements = document.querySelectorAll('.scan-figure, .figura-scan, [id*="scan"], [class*="scan"]');
        scanElements.forEach(element => {
          if (element && element.style) {
            debugLog('🔍 Nascondendo elemento scan: ' + element.tagName);
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
          }
        });
      };
      
      const showImage = () => {
        debugLog('👁️ Mostrando immagine scan', 'warn');
        const targetImageOverlay = document.getElementById('targetImageOverlay');
        if (targetImageOverlay) {
          targetImageOverlay.style.display = 'block';
          debugLog('✅ Scan mostrato');
        } else {
          debugLog('❌ targetImageOverlay non trovato', 'error');
        }
        
        // Cerca e mostra anche eventuali elementi di scan
        const scanElements = document.querySelectorAll('.scan-figure, .figura-scan, [id*="scan"], [class*="scan"]');
        scanElements.forEach(element => {
          if (element && element.style) {
            debugLog('🔍 Mostrando elemento scan: ' + element.tagName);
            element.style.display = 'block';
            element.style.visibility = 'visible';
            element.style.opacity = '1';
          }
        });
      };

      // Variabili di stato
      let found = [];
      let videoPlaying = false;

      // Eventi AR
      scene.addEventListener('xrimagefound', ({detail}) => {
        debugLog('🎯 Target Found: ' + detail.name, 'success');
        
        if (!found.includes(detail.name)) {
          found.push(detail.name);
          debugLog('📝 Target aggiunto alla lista: ' + detail.name);
        }
        
        // Se almeno un marker è trovato, nascondi l'immagine
        if (found.length > 0) {
          debugLog('✅ Target trovato - nascondo scan', 'success');
          hideImage();
        }
      });
      
      scene.addEventListener('xrimagelost', ({detail}) => {
        debugLog('❌ Target Lost: ' + detail.name, 'error');
        const index = found.indexOf(detail.name);
        if (index !== -1) {
          found.splice(index, 1);
          debugLog('📝 Target rimosso dalla lista: ' + detail.name);
        }
        
        // Se non ci sono più marker trovati, mostra l'immagine
        if (found.length === 0 && !videoPlaying) {
          debugLog('❌ Nessun target trovato - mostro scan', 'warn');
          showImage();
        }
      });
      
      // Setup eventi video con retry - SUPPORTO MULTITARGET
      const setupVideoEvents = () => {
        // Cerca sia video singolo che multitarget
        const singleVideo = document.querySelector('#video');
        const multiVideos = document.querySelectorAll('video[id^="video"]'); // video0, video1, video2, etc.
        
        const allVideos = singleVideo ? [singleVideo] : Array.from(multiVideos);
        
        if (allVideos.length > 0) {
          debugLog('✅ Setup eventi per ' + allVideos.length + ' video(s)', 'success');
          
          allVideos.forEach((video, index) => {
            const videoId = video.id || 'video' + index;
            debugLog('🎬 Configurando eventi per ' + videoId);
            
            video.addEventListener('play', () => {
              debugLog('▶️ Video PLAY: ' + videoId, 'success');
              videoPlaying = true;
              hideImage();
            });
            
            video.addEventListener('pause', () => {
              debugLog('⏸️ Video PAUSE: ' + videoId, 'warn');
              videoPlaying = false;
              if (found.length === 0) {
                showImage();
              }
            });
            
            video.addEventListener('ended', () => {
              debugLog('⏹️ Video ENDED: ' + videoId, 'warn');
              videoPlaying = false;
              if (found.length === 0) {
                showImage();
              }
            });
            
            video.addEventListener('loadstart', () => debugLog('📼 Video Loading: ' + videoId));
            video.addEventListener('canplay', () => debugLog('📼 Video Can Play: ' + videoId));
            
            // Eventi specifici 8th Wall
            video.addEventListener('xrextras-video-fade-in', () => {
              debugLog('🎬 Video fade in: ' + videoId, 'success');
              videoPlaying = true;
              hideImage();
            });
            
            video.addEventListener('xrextras-video-fade-out', () => {
              debugLog('🎬 Video fade out: ' + videoId, 'warn');
              videoPlaying = false;
              if (found.length === 0) {
                showImage();
              }
            });
          });
          
        } else {
          debugLog('⏳ Nessun video trovato, riprovo tra 500ms...', 'warn');
          setTimeout(setupVideoEvents, 500);
        }
      };
      
      setupVideoEvents();
    }
  })

  // Componente di debug per eventi touch
  registerComponentOnce('touch-debug', {
    init() {
      console.log('🖐️ Touch Debug Component inizializzato');
      
      // Verifica componenti 8th Wall
      const checkXRExtrasComponents = () => {
        const requiredComponents = ['xrextras-gesture-detector', 'xrextras-hold-drag', 'xrextras-two-finger-rotate', 'xrextras-pinch-scale'];
        requiredComponents.forEach(comp => {
          const hasComponent = AFRAME.components[comp];
          console.log(`🔍 Componente ${comp}:`, hasComponent ? '✅ CARICATO' : '❌ MANCANTE');
        });
      };
      
      setTimeout(checkXRExtrasComponents, 2000);
      
      // Test eventi touch base
      const testTouchEvents = () => {
        const model = document.querySelector('#model');
        if (model) {
          console.log('🎯 Modello trovato:', model);
          console.log('🎯 Classe cantap:', model.classList.contains('cantap'));
          console.log('🎯 Componenti attivi:', Object.keys(model.components || {}));
          
          // Test event listener manuale
          model.addEventListener('click', (e) => {
            console.log('🖱️ Click ricevuto sul modello!', e);
          });
          
          model.addEventListener('touchstart', (e) => {
            console.log('🖐️ Touchstart ricevuto sul modello!', e);
          });
        }
      };
      
      setTimeout(testTouchEvents, 3000);
    }
  });

  // Componente per splash screen e loading personalizzato
  registerComponentOnce('custom-qr-loading', {
    schema: {
      splashImage: {type: 'string', default: ''},
      showBackButton: {type: 'boolean', default: true}
    },
    
    init() {
      // Esegui tutto immediatamente prima che 8thWall si carichi
      this.loadingElement = null;
      this.isReady = false;
      this.startTime = Date.now();
      
      // PRIMA: crea splash screen subito
      this.createSplashScreen();
      
      // Rimuovi l'eventuale splash ARWEB iniziale (da ologramma-experience) dopo 1s
      setTimeout(() => {
        const arwebSplash = document.getElementById('arweb-splash');
        if (arwebSplash) {
          arwebSplash.style.transition = 'opacity 0.6s ease-out';
          arwebSplash.style.opacity = '0';
          setTimeout(() => arwebSplash.remove(), 600);
        }
      }, 1000);
      
      // SECONDA: nascondi aggressivamente 8thWall
      this.hide8thWallLoading();
      
      // TERZA: setup rilevamento
      this.setupReadyDetection();
    },
    
    hide8thWallLoading() {
      // Nascondi solo temporaneamente durante il nostro splash - VERSIONE MENO AGGRESSIVA
      const hideLoading = () => {
        const loadingSelectors = [
          '[xrextras-loading]',
          '.xrextras-loading', 
          '#xrextras-loading',
          '[landing-page]' // Aggiungi anche landing page
        ];
        
        loadingSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            if (el && el.style) {
              // Invece di nascondere completamente, rendiamo trasparente
              el.style.opacity = '0';
              el.style.pointerEvents = 'none'; // Importante!
            }
          });
        });
      };
      
      // Nascondi per i primi 2 secondi invece di 3
      hideLoading();
      setTimeout(hideLoading, 100);
      setTimeout(hideLoading, 500);
      setTimeout(hideLoading, 1000);
    },
    
    createSplashScreen() {
      const splashImage = this.data.splashImage || 'https://iscag-ei.it/wp-content/materiale/splash-loading.jpg';
      
      const splashDiv = document.createElement('div');
      splashDiv.id = 'custom-splash-screen';
      splashDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: url('${splashImage}') center/cover, linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        color: white;
        font-family: Arial, sans-serif;
        pointer-events: none;
      `;
      
      splashDiv.innerHTML = `
        <div style="position: absolute; bottom: 20%; width: 80%; text-align: center; pointer-events: none;">
          <h2 style="margin: 0 0 20px 0; font-size: 1.8em; text-shadow: 2px 2px 4px rgba(0,0,0,0.8);">Caricamento esperienza...</h2>
          <div style="width: 100%; height: 6px; background: rgba(255,255,255,0.3); border-radius: 3px; overflow: hidden; margin-bottom: 10px;">
            <div id="custom-progress-bar" style="width: 0%; height: 100%; background: #4CAF50; transition: width 0.5s ease; border-radius: 3px;"></div>
          </div>
          <p style="margin: 0; opacity: 0.9; font-size: 0.9em;">Inizializzazione in corso...</p>
        </div>
      `;
      
      document.body.appendChild(splashDiv);
      this.loadingElement = splashDiv;
      
      // Mostra subito il pulsante indietro se richiesto
      if (this.data.showBackButton) {
        this.createBackButton();
      }
      
      // Avvia progresso
      setTimeout(() => this.updateProgress(20), 200);
    },
    
    createBackButton() {
      const backBtn = document.createElement('button');
      backBtn.id = 'custom-back-btn';
      backBtn.innerHTML = '← Indietro';
      backBtn.style.cssText = `
        position: fixed;
        top: 20px;
        left: 20px;
        background: rgba(0, 0, 0, 0.7);
        border: 2px solid white;
        color: white;
        padding: 12px 20px;
        border-radius: 25px;
        cursor: pointer;
        font-size: 1.1em;
        z-index: 10001;
        backdrop-filter: blur(10px);
        pointer-events: auto;
      `;
      
      backBtn.onclick = () => {
        window.history.back();
      };
      
      document.body.appendChild(backBtn);
    },
    
    setupReadyDetection() {
      const scene = this.el;
      
      // Ascolta eventi 8thWall
      if (window.XR8) {
        XR8.addCameraPipelineModule({
          name: 'splash-loading-detector',
          onStart: () => {
            this.updateProgress(50);
          },
          onCameraStatusChange: ({status}) => {
            if (status === 'requesting') this.updateProgress(70);
            if (status === 'hasStream') this.updateProgress(85);
          },
          onRealityReady: () => {
            this.updateProgress(100);
            setTimeout(() => this.hideSplash(), 1000);
          }
        });
      }
      
      // Fallback: controlla se scene è pronta manualmente
      this.checkSceneReadyInterval = setInterval(() => {
        if (!this.isReady) {
          this.checkIfSceneReady();
        }
      }, 500);
      
      // Timeout finale più breve per evitare interferenze
      setTimeout(() => {
        if (!this.isReady) {
          console.log('⏰ Timeout splash screen raggiunto, forzo la chiusura');
          this.updateProgress(100);
          this.hideSplash();
        }
      }, 12000);
    },
    
    checkIfSceneReady() {
      const camera = document.querySelector('a-camera');
      // Consider only entity models; <a-scene> may carry gltf-model config attributes and should not be treated as a model.
      const models = document.querySelectorAll('a-entity[gltf-model]');
      const timeElapsed = Date.now() - this.startTime;
      
      let modelsLoaded = true;
      models.forEach(model => {
        if (!model.getObject3D('mesh')) {
          modelsLoaded = false;
        }
      });
      
      // Pronto se: camera OK + modelli caricati + minimo 2s passati
      if (camera && modelsLoaded && timeElapsed >= 2000) {
        this.updateProgress(100);
        setTimeout(() => this.hideSplash(), 500);
      } else if (timeElapsed >= 1000) {
        // Progresso graduale basato su tempo
        const progressPercent = Math.min(95, 30 + (timeElapsed / 100));
        this.updateProgress(progressPercent);
      }
    },
    
    updateProgress(percent) {
      const progressBar = document.getElementById('custom-progress-bar');
      if (progressBar) {
        progressBar.style.width = percent + '%';
      }
    },
    
    hideSplash() {
      if (this.loadingElement && !this.isReady) {
        this.isReady = true;
        
        // Ferma il controllo periodico
        if (this.checkSceneReadyInterval) {
          clearInterval(this.checkSceneReadyInterval);
        }
        
        // Ripristina elementi di 8thWall se necessario prima del fade out
        const restoreElements = () => {
          const loadingSelectors = [
            '[xrextras-loading]',
            '.xrextras-loading',
            '#xrextras-loading',
            '[landing-page]'
          ];
          
          loadingSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              if (el && el.style) {
                el.style.display = '';
                el.style.visibility = '';
                el.style.opacity = '';
                el.style.pointerEvents = ''; // Ripristina anche pointer events
              }
            });
          });
        };
        
        // Fade out
        this.loadingElement.style.opacity = '0';
        this.loadingElement.style.transition = 'opacity 0.8s ease';
        
        setTimeout(() => {
          if (this.loadingElement && this.loadingElement.parentNode) {
            this.loadingElement.parentNode.removeChild(this.loadingElement);
          }
          // Ripristina eventuali elementi nascosti dopo la rimozione
          restoreElements();
          
          // Forza re-inizializzazione eventi touch
          this.reinitializeTouchEvents();
        }, 800);
      }
    },
    
    reinitializeTouchEvents() {
      console.log('🔄 Re-inizializzazione eventi touch...');
      
      // Trova il gesture detector
      const scene = document.querySelector('a-scene');
      if (scene && scene.components && scene.components['xrextras-gesture-detector']) {
        console.log('🔄 Re-init gesture detector');
        try {
          // Forza re-init del gesture detector
          scene.components['xrextras-gesture-detector'].init();
        } catch (e) {
          console.warn('⚠️ Errore re-init gesture detector:', e);
        }
      }
      
      // Trova il modello e ri-attiva i componenti touch
      const model = document.querySelector('#model');
      if (model) {
        console.log('🔄 Re-init componenti modello');
        const touchComponents = ['xrextras-hold-drag', 'xrextras-two-finger-rotate', 'xrextras-pinch-scale'];
        
        touchComponents.forEach(comp => {
          if (model.components && model.components[comp]) {
            try {
              console.log(`🔄 Re-init ${comp}`);
              model.components[comp].init();
            } catch (e) {
              console.warn(`⚠️ Errore re-init ${comp}:`, e);
            }
          }
        });
        
        // Assicurati che il modello abbia la classe cantap
        if (!model.classList.contains('cantap')) {
          model.classList.add('cantap');
          console.log('🔄 Aggiunta classe cantap al modello');
        }
      }
      
      console.log('✅ Re-inizializzazione eventi touch completata');
    }
  });


  // Componente per debug delle envmap - DISABILITATO PER PERFORMANCE
  registerComponentOnce('envmap-debug', {
    init() {
      console.log('ℹ️ ENVMAP DEBUG COMPONENT - DISABILITATO PER PERFORMANCE');
      // Debug disabilitato per evitare che blocchi 8thwall
      return;
    },
    
    debugEnvmapStatus() {
      console.log('🔍 === === === DEBUG ENVMAP STATUS === === ===');
      console.log('🔍 Timestamp debug:', new Date().toISOString());
      console.log('🔍 URL corrente:', window.location.href);
      console.log('🔍 User Agent:', navigator.userAgent);
      
      // Controlla gli asset delle envmap
      const envmapAssets = [
        'envmap-pos-x', 'envmap-neg-x', 
        'envmap-pos-y', 'envmap-neg-y', 
        'envmap-pos-z', 'envmap-neg-z'
      ];
      
      console.log('🔍 === === VERIFICA ASSET ENVMAP === ===');
      envmapAssets.forEach(id => {
        const asset = document.querySelector(`#${id}`);
        if (asset) {
          const src = asset.getAttribute('src');
          const complete = asset.complete;
          const naturalWidth = asset.naturalWidth;
          const naturalHeight = asset.naturalHeight;
          const readyState = asset.readyState;
          const tagName = asset.tagName;
          const className = asset.className;
          const style = asset.getAttribute('style');
          
          console.log(`✅ Asset ${id}:`, {
            src: src,
            complete: complete,
            naturalWidth: naturalWidth,
            naturalHeight: naturalHeight,
            readyState: readyState,
            tagName: tagName,
            className: className,
            style: style,
            element: asset
          });
          
          // Controlla se l'immagine è caricata
          if (asset.complete) {
            console.log(`  📊 Immagine caricata: ${asset.naturalWidth}x${asset.naturalHeight}`);
            
            // Verifica che le dimensioni siano ragionevoli
            if (asset.naturalWidth < 10 || asset.naturalHeight < 10) {
              console.warn(`  ⚠️ Dimensioni sospette: ${asset.naturalWidth}x${asset.naturalHeight}`);
            }
          } else {
            console.log(`  ⏳ Immagine in caricamento...`);
            
            // Aggiungi event listener per il caricamento
            asset.addEventListener('load', () => {
              console.log(`  ✅ Immagine ${id} caricata con successo: ${asset.naturalWidth}x${asset.naturalHeight}`);
            });
            
            asset.addEventListener('error', (error) => {
              console.error(`  ❌ Errore caricamento immagine ${id}:`, error);
            });
          }
        } else {
          console.log(`❌ Asset ${id}: NON TROVATO`);
          
          // Cerca elementi simili
          const similarElements = document.querySelectorAll(`[id*="envmap"], [id*="ENVMAP"], [id*="Envmap"]`);
          if (similarElements.length > 0) {
            console.log(`  🔍 Elementi simili trovati:`, similarElements);
          }
        }
      });
      
      // Controlla il cubemap
      console.log('🔍 === === VERIFICA CUBEMAP === ===');
      const cubemap = document.querySelector('#reflection');
      if (cubemap) {
        console.log('✅ Cubemap trovato:', cubemap);
        console.log('  📊 ID:', cubemap.id);
        console.log('  📊 TagName:', cubemap.tagName);
        console.log('  📊 Attributi:', cubemap.getAttributeNames());
        console.log('  📊 Posizione:', cubemap.getAttribute('position'));
        console.log('  📊 Rotazione:', cubemap.getAttribute('rotation'));
        console.log('  📊 Scala:', cubemap.getAttribute('scale'));
        
        // Verifica le referenze alle texture
        const posx = cubemap.getAttribute('posx');
        const negx = cubemap.getAttribute('negx');
        const posy = cubemap.getAttribute('posy');
        const negy = cubemap.getAttribute('negy');
        const posz = cubemap.getAttribute('posz');
        const negz = cubemap.getAttribute('negz');
        
        console.log('  📊 Referenze texture:', { posx, negx, posy, negy, posz, negz });
        
        // Verifica che le referenze puntino a elementi esistenti
        [posx, negx, posy, negy, posz, negz].forEach(ref => {
          if (ref) {
            const targetElement = document.querySelector(ref);
            console.log(`  📊 Referenza ${ref}:`, targetElement ? 'TROVATA' : 'NON TROVATA');
          }
        });
        
      } else {
        console.log('❌ Cubemap NON TROVATO');
        
        // Cerca elementi simili
        const similarElements = document.querySelectorAll('[id*="reflection"], [id*="REFLECTION"], [id*="Reflection"]');
        if (similarElements.length > 0) {
          console.log('  🔍 Elementi simili trovati:', similarElements);
        }
      }
      
      // Controlla la sfera di prova
      console.log('🔍 === === VERIFICA ENVPROBE === ===');
      const envProbe = document.querySelector('#envProbe');
      if (envProbe) {
        console.log('✅ EnvProbe trovato:', envProbe);
        console.log('  📊 ID:', envProbe.id);
        console.log('  📊 TagName:', envProbe.tagName);
        console.log('  📊 Attributi:', envProbe.getAttributeNames());
        console.log('  📊 Posizione:', envProbe.getAttribute('position'));
        console.log('  📊 Rotazione:', envProbe.getAttribute('rotation'));
        console.log('  📊 Scala:', envProbe.getAttribute('scale'));
        console.log('  📊 Raggio:', envProbe.getAttribute('radius'));
        console.log('  📊 Visible:', envProbe.getAttribute('visible'));
        
        const material = envProbe.getAttribute('material');
        console.log('  🎨 Materiale:', material);
        
        // Verifica se ha un Object3D
        const probeMesh = envProbe.getObject3D('mesh');
        if (probeMesh) {
          console.log('  ✅ ProbeMesh trovata:', probeMesh);
          console.log('    📊 Tipo:', probeMesh.type);
          console.log('    📊 Children:', probeMesh.children.length);
          console.log('    📊 Visible:', probeMesh.visible);
          
          if (probeMesh.material) {
            console.log('    🎨 Materiale probe:', probeMesh.material);
            console.log('    🎨 EnvMap nel materiale:', probeMesh.material.envMap);
            console.log('    🎨 Metalness:', probeMesh.material.metalness);
            console.log('    🎨 Roughness:', probeMesh.material.roughness);
          } else {
            console.log('    ❌ Nessun materiale nella probe mesh');
          }
        } else {
          console.log('  ❌ ProbeMesh NON TROVATA');
        }
        
      } else {
        console.log('❌ EnvProbe NON TROVATO');
        
        // Cerca elementi simili
        const similarElements = document.querySelectorAll('[id*="probe"], [id*="PROBE"], [id*="Probe"], [id*="sphere"], [id*="SPHERE"]');
        if (similarElements.length > 0) {
          console.log('  🔍 Elementi simili trovati:', similarElements);
        }
      }
      
      // Controlla i modelli 3D
      console.log('🔍 === === VERIFICA MODELLI 3D === ===');
      const models = document.querySelectorAll('[gltf-model], [obj-model], [collada-model]');
      console.log(`📦 Modelli 3D trovati: ${models.length}`);
      
      models.forEach((model, index) => {
        console.log(`  📦 Modello ${index + 1}:`, {
          tagName: model.tagName,
          id: model.id,
          className: model.className,
          gltfModel: model.getAttribute('gltf-model'),
          objModel: model.getAttribute('obj-model'),
          colladaModel: model.getAttribute('collada-model')
        });
        
        const mesh = model.getObject3D('mesh');
        if (mesh) {
          console.log(`    ✅ Mesh 3D: ${mesh.children.length} children`);
          console.log(`    📊 Tipo mesh:`, mesh.type);
          console.log(`    📊 Visible:`, mesh.visible);
          console.log(`    📊 Posizione:`, mesh.position);
          
          // Verifica se ha il componente apply-cubemap
          const hasApplyCubemap = model.hasAttribute('apply-cubemap');
          console.log(`    🔧 Componente apply-cubemap:`, hasApplyCubemap ? 'PRESENTE' : 'ASSENTE');
          
          // Verifica i materiali
          let materialCount = 0;
          let envMapCount = 0;
          mesh.traverse((node) => {
            if (node.isMesh && node.material) {
              materialCount++;
              if (node.material.envMap) {
                envMapCount++;
              }
            }
          });
          console.log(`    🎨 Materiali: ${materialCount}, con EnvMap: ${envMapCount}`);
          
        } else {
          console.log(`    ❌ Mesh 3D: NON CARICATA`);
          
          // Verifica se ha il componente apply-cubemap
          const hasApplyCubemap = model.hasAttribute('apply-cubemap');
          console.log(`    🔧 Componente apply-cubemap:`, hasApplyCubemap ? 'PRESENTE' : 'ASSENTE');
        }
      });
      
      // Controlla i componenti registrati
      console.log('🔍 === === VERIFICA COMPONENTI REGISTRATI === ===');
      if (typeof AFRAME !== 'undefined' && AFRAME.components) {
        const registeredComponents = Object.keys(AFRAME.components);
        console.log(`📋 Componenti A-Frame registrati: ${registeredComponents.length}`);
        
        const envmapRelated = registeredComponents.filter(name => 
          name.includes('envmap') || name.includes('cubemap') || name.includes('apply')
        );
        console.log(`🔧 Componenti envmap/cubemap:`, envmapRelated);
        
        // Verifica specificamente apply-cubemap
        if (AFRAME.components['apply-cubemap']) {
          console.log('✅ Componente apply-cubemap: REGISTRATO');
        } else {
          console.log('❌ Componente apply-cubemap: NON REGISTRATO');
        }
      } else {
        console.log('❌ AFRAME non disponibile o componenti non accessibili');
      }
      
      // Controlla la scena A-Frame
      console.log('🔍 === === VERIFICA SCENA A-FRAME === ===');
      const scene = document.querySelector('a-scene');
      if (scene) {
        console.log('✅ A-Scene trovata:', scene);
        console.log('  📊 ID:', scene.id);
        console.log('  📊 Attributi:', scene.getAttributeNames());
        console.log('  📊 Renderer:', scene.getAttribute('renderer'));
        console.log('  📊 Sistema:', scene.getAttribute('system'));
        
        // Verifica se ha il componente envmap-debug
        const hasEnvmapDebug = scene.hasAttribute('envmap-debug');
        console.log('  🔧 Componente envmap-debug:', hasEnvmapDebug ? 'PRESENTE' : 'ASSENTE');
        
      } else {
        console.log('❌ A-Scene NON TROVATA');
      }
      
      // Controlla gli errori nella console
      console.log('🔍 === === VERIFICA ERRORI CONSOLE === ===');
      const originalError = console.error;
      const errors = [];
      
      console.error = function(...args) {
        errors.push({
          timestamp: new Date().toISOString(),
          args: args
        });
        originalError.apply(console, args);
      };
      
      // Aspetta un po' e poi mostra gli errori
      setTimeout(() => {
        console.log(`📊 Errori catturati: ${errors.length}`);
        errors.forEach((error, index) => {
          console.log(`  ❌ Errore ${index + 1}:`, error.args);
        });
        
        // Ripristina console.error originale
        console.error = originalError;
      }, 1000);
      
      console.log('🔍 === === === FINE DEBUG ENVMAP STATUS === === ===');
    }
  })

  // Componente di test per apply-cubemap - DISABILITATO PER PERFORMANCE
  registerComponentOnce('test-apply-cubemap', {
    init() {
      console.log('ℹ️ TEST APPLY-CUBEMAP COMPONENT - DISABILITATO PER PERFORMANCE');
      // Test disabilitati per evitare che blocchino 8thwall
      return;
    },
    
    verifyComponentRegistration() {
      console.log('🔍 === VERIFICA REGISTRAZIONE COMPONENTE ===');
      
      if (typeof AFRAME !== 'undefined' && AFRAME.components) {
        const hasComponent = AFRAME.components['apply-cubemap'];
        if (hasComponent) {
          console.log('✅ Componente apply-cubemap: REGISTRATO');
          console.log('✅ Dettagli componente:', hasComponent);
          
          // Verifica che abbia i metodi necessari
          const hasInit = typeof hasComponent.init === 'function';
          const hasSchema = hasComponent.schema;
          
          console.log('✅ Metodi disponibili:', {
            init: hasInit,
            schema: !!hasSchema
          });
          
        } else {
          console.error('❌ Componente apply-cubemap: NON REGISTRATO');
          console.error('❌ Componenti disponibili:', Object.keys(AFRAME.components));
        }
      } else {
        console.error('❌ AFRAME non disponibile');
      }
    },
    
    testComponentFunctionality() {
      console.log('🧪 === TEST FUNZIONALITÀ COMPONENTE ===');
      
      // Cerca elementi con il componente apply-cubemap
      const elementsWithComponent = document.querySelectorAll('[apply-cubemap]');
      console.log(`🧪 Elementi con apply-cubemap: ${elementsWithComponent.length}`);
      
      elementsWithComponent.forEach((element, index) => {
        console.log(`🧪 Elemento ${index + 1}:`, {
          tagName: element.tagName,
          id: element.id,
          className: element.className,
          gltfModel: element.getAttribute('gltf-model'),
          position: element.getAttribute('position'),
          rotation: element.getAttribute('rotation')
        });
        
        // Verifica se ha una mesh
        const mesh = element.getObject3D('mesh');
        if (mesh) {
          console.log(`  ✅ Mesh trovata: ${mesh.children.length} children`);
          
          // Verifica i materiali
          let materialCount = 0;
          let envMapCount = 0;
          mesh.traverse((node) => {
            if (node.isMesh && node.material) {
              materialCount++;
              if (node.material.envMap) {
                envMapCount++;
              }
            }
          });
          console.log(`  🎨 Materiali: ${materialCount}, con EnvMap: ${envMapCount}`);
          
        } else {
          console.log(`  ❌ Mesh NON TROVATA`);
        }
      });
      
      // Test manuale del componente
      this.testManualComponent();
    },
    
    testManualComponent() {
      console.log('🧪 === TEST MANUALE COMPONENTE ===');
      
      try {
        // Prova a creare un'istanza del componente
        if (AFRAME.components['apply-cubemap']) {
          console.log('🧪 Creazione istanza test del componente...');
          
          // Crea un elemento di test
          const testElement = document.createElement('a-entity');
          testElement.id = 'test-apply-cubemap';
          testElement.setAttribute('apply-cubemap', '');
          
          // Aggiungi alla scena
          const scene = document.querySelector('a-scene');
          if (scene) {
            scene.appendChild(testElement);
            console.log('✅ Elemento di test aggiunto alla scena');
            
            // Verifica che il componente sia inizializzato
            setTimeout(() => {
              const component = testElement.components['apply-cubemap'];
              if (component) {
                console.log('✅ Componente test inizializzato:', component);
                console.log('✅ Metodi disponibili:', Object.getOwnPropertyNames(component));
              } else {
                console.error('❌ Componente test non inizializzato');
              }
              
              // Rimuovi l'elemento di test
              scene.removeChild(testElement);
              console.log('✅ Elemento di test rimosso');
            }, 1000);
            
          } else {
            console.error('❌ Scena non trovata per il test');
          }
        }
      } catch (error) {
        console.error('❌ Errore nel test manuale:', error);
        console.error('❌ Stack trace:', error.stack);
      }
    }
  })

  // Sistema di debug globale per le envmap - DISABILITATO PER PERFORMANCE
  registerComponentOnce('global-envmap-debug', {
    init() {
      console.log('ℹ️ GLOBAL ENVMAP DEBUG COMPONENT - DISABILITATO PER PERFORMANCE');
      // Debug globale disabilitato per evitare che blocchi 8thwall
      return;
    },
    
    globalDebug() {
      console.log('🌍 === === === DEBUG GLOBALE ENVMAP === === ===');
      console.log('🌍 Timestamp:', new Date().toISOString());
      
      // Verifica A-Frame
      this.checkAFrame();
      
      // Verifica componenti
      this.checkComponents();
      
      // Verifica elementi nella scena
      this.checkSceneElements();
      
      // Verifica errori
      this.checkErrors();
      
      console.log('🌍 === === === FINE DEBUG GLOBALE === === ===');
    },
    
    checkAFrame() {
      console.log('🔍 === VERIFICA A-FRAME ===');
      
      if (typeof AFRAME !== 'undefined') {
        console.log('✅ AFRAME disponibile');
        console.log('✅ Versione:', AFRAME.version);
        console.log('✅ Componenti registrati:', Object.keys(AFRAME.components).length);
        console.log('✅ Shader registrati:', Object.keys(AFRAME.shaders).length);
        console.log('✅ Geometrie registrate:', Object.keys(AFRAME.geometries).length);
        console.log('✅ Materiali registrati:', Object.keys(AFRAME.materials).length);
      } else {
        console.error('❌ AFRAME NON DISPONIBILE');
      }
      
      // Verifica Three.js
      if (typeof THREE !== 'undefined') {
        console.log('✅ THREE.js disponibile');
        console.log('✅ Versione:', THREE.REVISION);
      } else {
        console.error('❌ THREE.js NON DISPONIBILE');
      }
    },
    
    checkComponents() {
      console.log('🔍 === VERIFICA COMPONENTI ENVMAP ===');
      
      const requiredComponents = ['apply-cubemap', 'envmap-debug', 'test-apply-cubemap'];
      
      requiredComponents.forEach(componentName => {
        if (AFRAME.components && AFRAME.components[componentName]) {
          console.log(`✅ Componente ${componentName}: REGISTRATO`);
          
          const component = AFRAME.components[componentName];
          console.log(`  📊 Metodi:`, Object.getOwnPropertyNames(component));
          console.log(`  📊 Schema:`, component.schema);
          
        } else {
          console.error(`❌ Componente ${componentName}: NON REGISTRATO`);
        }
      });
    },
    
    checkSceneElements() {
      console.log('🔍 === VERIFICA ELEMENTI SCENA ===');
      
      // Verifica scena
      const scene = document.querySelector('a-scene');
      if (scene) {
        console.log('✅ A-Scene trovata');
        console.log('  📊 ID:', scene.id);
        console.log('  📊 Attributi:', scene.getAttributeNames());
        console.log('  📊 Componenti attivi:', Object.keys(scene.components || {}));
        
        // Verifica se ha i componenti di debug
        const hasEnvmapDebug = scene.hasAttribute('envmap-debug');
        const hasTestApplyCubemap = scene.hasAttribute('test-apply-cubemap');
        
        console.log('  🔧 Componenti debug:', {
          envmapDebug: hasEnvmapDebug,
          testApplyCubemap: hasTestApplyCubemap
        });
        
      } else {
        console.error('❌ A-Scene NON TROVATA');
      }
      
      // Verifica modelli 3D
      const models = document.querySelectorAll('[gltf-model], [obj-model], [collada-model]');
      console.log(`📦 Modelli 3D: ${models.length}`);
      
      models.forEach((model, index) => {
        const hasApplyCubemap = model.hasAttribute('apply-cubemap');
        const mesh = model.getObject3D('mesh');
        
        console.log(`  📦 Modello ${index + 1}:`, {
          id: model.id,
          type: model.getAttribute('gltf-model') || model.getAttribute('obj-model') || model.getAttribute('collada-model'),
          hasApplyCubemap: hasApplyCubemap,
          hasMesh: !!mesh,
          meshChildren: mesh ? mesh.children.length : 0
        });
      });
      
      // Verifica asset envmap
      const envmapAssets = document.querySelectorAll('[id*="envmap"]');
      console.log(`🌍 Asset envmap: ${envmapAssets.length}`);
      
      envmapAssets.forEach((asset, index) => {
        console.log(`  🌍 Asset ${index + 1}:`, {
          id: asset.id,
          tagName: asset.tagName,
          src: asset.getAttribute('src'),
          complete: asset.complete,
          naturalWidth: asset.naturalWidth,
          naturalHeight: asset.naturalHeight
        });
      });
    },
    
    checkErrors() {
      console.log('🔍 === VERIFICA ERRORI ===');
      
      // Controlla errori JavaScript
      const originalError = console.error;
      const originalWarn = console.warn;
      
      let errorCount = 0;
      let warningCount = 0;
      
      console.error = function(...args) {
        errorCount++;
        originalError.apply(console, args);
      };
      
      console.warn = function(...args) {
        warningCount++;
        originalWarn.apply(console, args);
      };
      
      // Mostra conteggio errori dopo un delay
      setTimeout(() => {
        console.log(`📊 Errori catturati: ${errorCount}`);
        console.log(`📊 Warning catturati: ${warningCount}`);
        
        // Ripristina console originali
        console.error = originalError;
        console.warn = originalWarn;
      }, 2000);
    }
  })

  // Registra shader chromakey personalizzato
  // Chiama la funzione di registrazione lazy
  try {
    console.log('🎭 Registrazione shader chromakey personalizzato...')
    registerChromakeyShader()
    
    // Verifica che sia effettivamente registrato dopo un breve delay
    setTimeout(() => {
      if (typeof AFRAME !== 'undefined' && AFRAME.shaders && AFRAME.shaders.chromakey) {
        console.log('✅ Shader chromakey verificato e disponibile')
      } else {
        console.warn('⚠️ Shader chromakey non ancora disponibile in AFRAME.shaders')
      }
    }, 200)
  } catch (e) {
    console.warn('⚠️ Errore nella registrazione shader chromakey:', e)
  }

  // Ologramma-specific components - Importazione diretta per evitare problemi
  // I componenti vengono importati all'inizio del file
  try {
    console.log('🎭 Registrazione componenti ologramma...')
    registerComponentOnce('play-video-correct', playVideoCorrectComponent)
    registerComponentOnce('custom-hold-drag', holdDragComponent)
    registerComponentOnce('look-at', lookAtComponent)
    console.log('✅ Componenti ologramma registrati con successo')
  } catch (e) {
    console.error('❌ Errore nel caricamento componenti ologramma:', e)
    // Fallback: registra componenti base se l'import fallisce
    registerComponentOnce('play-video-correct', {
      schema: { video: {type: 'string'}, autoplay: {type: 'boolean', default: true} },
      init() {
        console.warn('Componente play-video-correct fallback attivato')
        const videoEl = document.querySelector(this.data.video)
        if (videoEl && this.data.autoplay) {
          videoEl.play().catch(console.warn)
        }
      }
    })
  }
}

// Funzione per verificare se un componente è registrato
export const isComponentRegistered = (name) => {
  return registeredComponents.has(name)
}

// Funzione per ottenere la lista dei componenti registrati
export const getRegisteredComponents = () => {
  return Array.from(registeredComponents)
} 